const safeArray = (value) => (Array.isArray(value) ? value : []);

const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const normalizeTextValue = (value = "") => String(value || "").trim().toLowerCase();

const formatApiSalaryMonthValue = (salaryYear, salaryMonth) => {
  const normalizedYear = Number(salaryYear);
  const normalizedMonth = Number(salaryMonth);

  if (!Number.isFinite(normalizedYear) || !Number.isFinite(normalizedMonth)) {
    return "";
  }

  return `${normalizedYear}-${String(normalizedMonth).padStart(2, "0")}`;
};

const buildApiDriverPaymentAllowances = (payment = {}) => {
  const explicitAllowances = safeArray(
    payment?.allowances || payment?.salary_allowances || payment?.payment_allowances
  );

  if (explicitAllowances.length) {
    return explicitAllowances.map((allowance, index) => ({
      id:
        allowance?.id ||
        `${payment?.id || "driver-payment"}-allowance-${index + 1}`,
      title:
        allowance?.title ||
        allowance?.name ||
        allowance?.allowance_name ||
        allowance?.type ||
        "Allowance",
      type:
        allowance?.type ||
        allowance?.title ||
        allowance?.name ||
        allowance?.allowance_name ||
        "Allowance",
      amount: toNumber(allowance?.amount),
      source: allowance?.source || "API",
    }));
  }

  const totalAllowances = toNumber(payment?.total_allowances);
  const tripIncentive = toNumber(payment?.trip_incentive);
  const rows = [];

  if (tripIncentive > 0) {
    rows.push({
      id: `${payment?.id || "driver-payment"}-trip-incentive`,
      title: "Trip Incentive",
      type: "trip_incentive",
      amount: tripIncentive,
      source: "API",
    });
  }

  const remainingAllowances = Math.max(0, totalAllowances - tripIncentive);
  if (remainingAllowances > 0) {
    rows.push({
      id: `${payment?.id || "driver-payment"}-other-allowances`,
      title: "Other Allowances",
      type: "other_allowances",
      amount: remainingAllowances,
      source: "API",
    });
  }

  return rows;
};

const buildApiDriverPaymentDeductions = (payment = {}, monthLabel = "") => {
  const explicitDeductions = safeArray(
    payment?.deductions || payment?.salary_deductions || payment?.payment_deductions
  );

  if (explicitDeductions.length) {
    return explicitDeductions.map((deduction, index) => ({
      id:
        deduction?.id ||
        `${payment?.id || "driver-payment"}-deduction-${index + 1}`,
      title:
        deduction?.title ||
        deduction?.reason ||
        deduction?.name ||
        deduction?.deduction_name ||
        "Deduction",
      subtitle:
        deduction?.subtitle ||
        deduction?.description ||
        (deduction?.reason ? "Manual Deduction" : "") ||
        (monthLabel ? `Payroll Deduction - ${monthLabel}` : "Payroll Deduction"),
      amount: toNumber(deduction?.amount),
      createdAt: deduction?.created_at || deduction?.updated_at || "",
    }));
  }

  const totalDeductions = toNumber(payment?.total_deductions);
  if (totalDeductions <= 0) {
    return [];
  }

  return [
    {
      id: `${payment?.id || "driver-payment"}-total-deductions`,
      title: "Total Deductions",
      subtitle: monthLabel ? `Payroll Deduction - ${monthLabel}` : "Payroll Deduction",
      amount: totalDeductions,
      createdAt: payment?.payment_date || payment?.updated_at || payment?.created_at || "",
    },
  ];
};

const buildApiDriverPaymentViolations = (payment = {}) =>
  safeArray(payment?.violations || payment?.salary_violations || payment?.driver_violations).map(
    (violation, index) => ({
      id:
        violation?.id ||
        `${payment?.id || "driver-payment"}-violation-${index + 1}`,
      title: violation?.title || violation?.name || violation?.violation_name || "Violation",
      source: violation?.source || violation?.module || "API",
      incidents: toNumber(
        violation?.incidents,
        toNumber(violation?.count, toNumber(violation?.total, 0))
      ),
    })
  );

const buildApiDriverPaymentHistory = (payment = {}) =>
  safeArray(payment?.history_items || payment?.history || payment?.comments);

const resolveDriverPaymentActivityColor = (entry = {}) => {
  const activityText = normalizeTextValue(
    `${entry?.title || ""} ${entry?.message || ""} ${entry?.description || ""} ${entry?.after || ""}`
  );
  const explicitColor = normalizeTextValue(entry?.color);

  if (activityText.includes("deduction")) {
    return "red";
  }

  if (activityText.includes("allowance")) {
    return "green";
  }

  if (
    activityText.includes("violation") ||
    activityText.includes("incident") ||
    activityText.includes("compliance")
  ) {
    return "yellow";
  }

  if (["red", "green", "yellow", "blue"].includes(explicitColor)) {
    return explicitColor;
  }

  return "blue";
};

const buildApiDriverPaymentActivity = (payment = {}) =>
  safeArray(payment?.activity_log || payment?.activity || payment?.logs).map((entry, index) => ({
    id: entry?.id || `${payment?.id || "driver-payment"}-activity-${index + 1}`,
    title: entry?.title || entry?.message || entry?.description || entry?.after || "-",
    by:
      entry?.by ||
      entry?.user?.username ||
      entry?.created_by_user?.username ||
      entry?.author?.username ||
      "System",
    time: entry?.time || entry?.created_at || entry?.updated_at || "",
    color: resolveDriverPaymentActivityColor(entry),
  }));

export const formatAmount = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export const formatSalaryMonthLabel = (value = "") => {
  const [year, month] = String(value || "").split("-");
  const normalizedYear = Number(year);
  const normalizedMonth = Number(month);

  if (!Number.isFinite(normalizedYear) || !Number.isFinite(normalizedMonth)) {
    return String(value || "");
  }

  const resolvedDate = new Date(normalizedYear, Math.max(normalizedMonth - 1, 0), 1);
  if (Number.isNaN(resolvedDate.getTime())) return String(value || "");

  return resolvedDate.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
};

export const formatDisplayDate = (value = "") => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value || "");

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const computeDriverPaymentTotals = (record = {}) => {
  const allowances = safeArray(record.allowances);
  const deductions = safeArray(record.deductions);
  const baseSalary = toNumber(record?.baseSalary ?? record?.base_salary);
  const hasExplicitAllowances =
    record?.totalAllowances !== undefined || record?.total_allowances !== undefined;
  const hasExplicitDeductions =
    record?.totalDeductions !== undefined || record?.total_deductions !== undefined;
  const hasExplicitNetPay =
    record?.netPay !== undefined || record?.net_pay_salary !== undefined;
  const totalAllowances = hasExplicitAllowances
    ? toNumber(record?.totalAllowances ?? record?.total_allowances)
    : allowances.reduce((sum, allowance) => sum + toNumber(allowance?.amount), 0);
  const totalDeductions = hasExplicitDeductions
    ? toNumber(record?.totalDeductions ?? record?.total_deductions)
    : deductions.reduce((sum, deduction) => sum + toNumber(deduction?.amount), 0);
  const netPay = hasExplicitNetPay
    ? toNumber(record?.netPay ?? record?.net_pay_salary)
    : baseSalary + totalAllowances - totalDeductions;

  return {
    baseSalary,
    totalAllowances,
    totalDeductions,
    netPay,
  };
};

export const buildDriverPaymentRecordFromApi = (payment = {}) => {
  const monthValue = formatApiSalaryMonthValue(payment?.salary_year, payment?.salary_month);
  const monthLabel =
    payment?.salary_master?.month_name || formatSalaryMonthLabel(monthValue);
  const expenseDateValue = payment?.payment_date || payment?.created_at || "";
  const allowances = buildApiDriverPaymentAllowances(payment);
  const deductions = buildApiDriverPaymentDeductions(payment, monthLabel);

  return {
    id: String(payment?.id ?? ""),
    paymentId: payment?.payment_id || "-",
    driverId: payment?.driver?.driver_info_id || String(payment?.driver_id ?? ""),
    driverRecordId: payment?.driver?.id ?? payment?.driver_id ?? 0,
    driverName:
      payment?.driver?.user?.username ||
      payment?.driver?.name ||
      payment?.driver?.full_name ||
      payment?.driver?.driver_info_id ||
      payment?.driver?.employee_id ||
      "-",
    driverImage: payment?.driver?.user?.image || "",
    monthValue,
    monthLabel,
    baseSalary: toNumber(payment?.base_salary),
    allowances,
    deductions,
    violations: buildApiDriverPaymentViolations(payment),
    totalAllowances: toNumber(payment?.total_allowances),
    totalDeductions: toNumber(payment?.total_deductions),
    netPay: toNumber(payment?.net_pay_salary),
    tripIncentive: toNumber(payment?.trip_incentive),
    status: String(payment?.status || "draft").trim().toLowerCase(),
    expenseDateValue,
    expenseDateDisplay: expenseDateValue ? formatDisplayDate(expenseDateValue) : "-",
    createdAt: payment?.created_at || "",
    issueDate: expenseDateValue,
    historyItems: buildApiDriverPaymentHistory(payment),
    activityLog: buildApiDriverPaymentActivity(payment),
    notes: payment?.notes ?? "",
    rawData: payment,
  };
};

export const buildDriverPaymentsOverview = (records = []) => {
  const normalizedRecords = safeArray(records);

  return {
    totalSalaries: normalizedRecords.reduce(
      (sum, record) => sum + computeDriverPaymentTotals(record).netPay,
      0
    ),
    pendingRuns: normalizedRecords.filter(
      (record) => ["pending", "draft"].includes(String(record?.status).toLowerCase())
    ).length,
    totalAllowances: normalizedRecords.reduce(
      (sum, record) => sum + computeDriverPaymentTotals(record).totalAllowances,
      0
    ),
    totalDeductions: normalizedRecords.reduce(
      (sum, record) => sum + computeDriverPaymentTotals(record).totalDeductions,
      0
    ),
  };
};

export const buildDriverPaymentTableRows = (records = []) =>
  safeArray(records).map((record) => {
    const totals = computeDriverPaymentTotals(record);

    return {
      ...record,
      baseSalary: totals.baseSalary,
      totalAllowances: totals.totalAllowances,
      totalDeductions: totals.totalDeductions,
      netPay: totals.netPay,
    };
  });

export const findDriverPaymentRecord = (records = [], recordId = "") =>
  safeArray(records).find(
    (record) =>
      String(record?.id) === String(recordId) ||
      String(record?.paymentId || record?.payment_id || "").toLowerCase() ===
        String(recordId).toLowerCase()
  ) || null;
